import { Link } from 'react-router-dom'
import { useUniversePath } from '../lib/universe'
import { formatPoints } from '../lib/decimal'
import type { RaceSummary, ResultItem, StandingItem } from '../api/types'

/**
 * The drivers' championship as Wikipedia draws it: one row per driver, one
 * column per round, each cell the race result coloured by what it was worth.
 *
 * Superscripts carry the rest: the sprint position when it scored, P for pole
 * and F for fastest lap. Results of free practice -- the FP Championship's --
 * get a column per session, each one a race of its own.
 */
export function ResultsMatrix({
  items,
  races,
  results,
}: {
  items: StandingItem[]
  races: RaceSummary[]
  results: ResultItem[]
}) {
  const rounds = columns(races, results)
  const to = useUniversePath()
  // ponytail: a shared car gives a driver two results in one round; the first
  // (better-placed) one wins the cell, as in most published tables.
  const race = new Map<string, ResultItem>()
  const sprint = new Map<string, ResultItem>()
  for (const result of results) {
    const sprinted = result.session_type === 'SPRINT_RACE'
    const into = sprinted ? sprint : race
    const key = `${result.driver.slug}|${result.round}`
    const at = sprinted ? key : `${key}|${result.session_type}`
    if (!into.has(at)) into.set(at, result)
  }

  return (
    <div className="table-scroll">
      <table className="grid matrix">
        <thead>
          <tr>
            <th scope="col" className="num">
              Pos
            </th>
            <th scope="col">Driver</th>
            {rounds.map(({ race: entry, session }) => (
              <th
                key={`${entry.round}|${session}`}
                scope="col"
                className="matrix__round"
                title={entry.grand_prix.name}
              >
                {entry.has_results ? (
                  <Link to={to(`/races/${entry.year}/${entry.round}`)}>
                    {roundLabel(entry)}
                  </Link>
                ) : (
                  roundLabel(entry)
                )}
                {session === 'RACE' ? null : (
                  <span className="matrix__session">FP{session.slice(-1)}</span>
                )}
                <span className="matrix__flag" aria-hidden="true">
                  {flag(entry.circuit.country?.alpha2_code)}
                </span>
              </th>
            ))}
            <th scope="col" className="num">
              Points
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr
              key={item.key}
              className={item.championship_won ? 'matrix__champion' : undefined}
            >
              <td className="num">{item.position === 0 ? 'excl' : item.position}</td>
              <th scope="row" className="matrix__driver">
                <Link to={to(`/drivers/${item.entity}`)}>{item.name ?? item.entity}</Link>
              </th>
              {rounds.map(({ race: entry, session }) => {
                const key = `${item.entity}|${entry.round}`
                const cell = race.get(`${key}|${session}`)
                return (
                  <td
                    key={`${entry.round}|${session}`}
                    className={cell ? `matrix__cell ${tone(cell)}` : 'matrix__cell'}
                  >
                    {cell ? <Cell result={cell} sprint={sprint.get(key)} /> : null}
                  </td>
                )
              })}
              <td className="num matrix__points">{formatPoints(item.points)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Cell({ result, sprint }: { result: ResultItem; sprint: ResultItem | undefined }) {
  const marks = [
    sprint && scored(sprint.points) ? sprint.position.text : '',
    result.pole_position ? 'P' : '',
    result.fastest_lap ? 'F' : '',
  ]
    .filter(Boolean)
    .join(' ')
  return (
    <>
      {label(result)}
      {marks ? <sup>{marks}</sup> : null}
    </>
  )
}

/** Wikipedia's wording: a retirement is "Ret", a classified non-finisher gets a dagger. */
export function label(result: ResultItem): string {
  const { status, text } = result.position
  if (status === 'DNF') return 'Ret'
  if (status === 'FIN' && result.reason_retired) return `${text}†`
  return text
}

/** The colour key every F1 results table uses. */
export function tone(result: ResultItem): string {
  const { status, number } = result.position
  if (status === 'FIN') {
    if (number === 1) return 'tone--gold'
    if (number === 2) return 'tone--silver'
    if (number === 3) return 'tone--bronze'
    return scored(result.points) ? 'tone--points' : 'tone--finish'
  }
  if (status === 'DNF' || status === 'NC') return 'tone--ret'
  if (status === 'DNQ' || status === 'DNPQ') return 'tone--dnq'
  if (status === 'DSQ' || status === 'EX') return 'tone--dsq'
  return ''
}

type Column = { race: RaceSummary; session: ResultItem['session_type'] }

/** One column per round, or per free practice session when that is what the results are. */
export function columns(races: RaceSummary[], results: ResultItem[]): Column[] {
  const held = new Map<number, Set<Column['session']>>()
  for (const result of results) {
    if (!result.session_type.startsWith('PRACTICE')) continue
    held.set(result.round, (held.get(result.round) ?? new Set()).add(result.session_type))
  }
  return [...races]
    .sort((a, b) => a.round - b.round)
    .flatMap((race) =>
      held.size
        ? [...(held.get(race.round) ?? [])].sort().map((session) => ({ race, session }))
        : [{ race, session: 'RACE' as const }],
    )
}

/** Non-zero without arithmetic: points are decimal strings (see lib/decimal). */
const scored = (points: string | null | undefined) => !!points && /[1-9]/.test(points)

const roundLabel = (race: RaceSummary) =>
  race.grand_prix.abbreviation ?? race.grand_prix.name.slice(0, 3).toUpperCase()

/** Two regional-indicator letters make a flag emoji. */
const flag = (alpha2: string | null | undefined) =>
  alpha2
    ? String.fromCodePoint(...[...alpha2.toUpperCase()].map((c) => 0x1f1a5 + c.charCodeAt(0)))
    : ''
